<template>
  <div id="app" :class="appClass" @drop.prevent @dragover.prevent="dragover" v-show="showApp">
    <div class="pageWrapper" v-show="!showOrientationBlock">
      <TopLevelLayout :inPhoneLandscapeMode="inPhoneLandscapeMode">
        <template #content>
          <router-view />
        </template>
      </TopLevelLayout>
      <SilentAuthFrame v-if="!isGuestUser"></SilentAuthFrame>
      <ConnectionErrorDialog />
      <DbInvalidatedDialog v-if="dbInvalidated" />
      <div id="portal-target"></div>
      <div id="meeting-portal"></div>
      <div id="meeting-settings-portal"></div>
      <div id="agent-portal"></div>
    </div>
    <OrientationBlock
      v-if="showOrientationBlock"
      :landscape="showLandscapeBlock"
      :portrait="showPortraitBlock" />
  </div>
</template>

<script setup lang="ts">
import { options } from 'floating-vue';
import { debounce } from 'lodash-es';
import {
  computed,
  ComputedRef,
  defineAsyncComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  Ref,
  ref,
  watch,
} from 'vue';

import { useFavicon } from '@/composables/app/Favicon';
import { useGoogleAnalytics } from '@/composables/app/GoogleAnalytics';
import { useHotjar } from '@/composables/app/Hotjar';
import { useOrientationDetection } from '@/composables/app/OrientationDetection';
import { usePageTitle } from '@/composables/app/PageTitle';
import { usePendo } from '@/composables/app/Pendo';
import { useServiceWorkerListener } from '@/composables/app/ServiceWorkerListener';
import { useRoute } from '@/composables/router/VueRouter';
import BrowserDetection from '@/data/BrowserDetection';
import Integrations from '@/data/config/Integrations';
import { GuestOnlineStatusDetails } from '@/data/datatypes/UserDetails';
import { eventSignalsLogout } from '@/data/helpers/TokenHelper';
import IosDeviceToken from '@/data/pushmessaging/IosDeviceToken';
import DataWorker from '@/data/storage/DataWorker';
import TopLevelLayout from '@/layouts/TopLevelLayout.vue';
import MatomoAnalytics from '@/mixins/tracking/MatomoAnalytics';
import RouteNames from '@/router/RouteNames';
import { useChatMessagesStore } from '@/stores/ChatMessages';
import { ChatViewPermissions } from '@/stores/ChatMessages.types';
import { useConnectionStore } from '@/stores/Connection';
import { DEFAULT_COLLABORATION_APP_ID } from '@/stores/Tasks';
import { useTrackEntriesStore } from '@/stores/TrackEntries';
import { useTracksStore } from '@/stores/Tracks';
import { useUIStateStore } from '@/stores/UIState';
import { useUserStore } from '@/stores/User';

const SilentAuthFrame = defineAsyncComponent(() => import('@/components/SilentAuthFrame.vue'));
const ConnectionErrorDialog = defineAsyncComponent(() => import('@/components/dialogs/ConnectionErrorDialog.vue'));
const OrientationBlock = defineAsyncComponent(() => import('@/views/OrientationBlock.vue'));
const DbInvalidatedDialog = defineAsyncComponent(() => import('@/components/dialogs/DbInvalidatedDialog.vue'));

useServiceWorkerListener();
useFavicon();
useHotjar();
useGoogleAnalytics();
usePendo();
usePageTitle();
const { inTabletPortraitMode, inPhoneLandscapeMode } = useOrientationDetection();

options.themes.tooltip.placement = 'top-start';
options.themes.error = {
  $extend: 'tooltip',
  placement: 'bottom-start',
  distance: '8',
  triggers: [],
  autoHide: false,
  autoSize: 'max',
  arrowPadding: 16,
};
options.themes.info = {
  $extend: 'tooltip',
  $resetCss: true,
  placement: 'top',
};

export interface GuestState {
  guestUser: boolean;
  guestMember: boolean;
  isAppGuestRoute: boolean;
}

const tracksStore = useTracksStore();
const trackEntriesStore = useTrackEntriesStore();
const userStore = useUserStore();
const chatMessagesStore = useChatMessagesStore();
const uiStateStore = useUIStateStore();
const connectionStore = useConnectionStore();

const route = useRoute();

const debouncedOverrideVh: () => void = debounce(overrideVh, 100, {
  maxWait: 200,
  leading: true,
  trailing: true,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const matomoAnalytics: Ref<{ unwatch: any; } | null> = ref(null);

const guestDetails: Ref<GuestOnlineStatusDetails | null> = ref(null);

const canViewCurrentChats: ComputedRef<ChatViewPermissions> = computed(() => {
  return chatMessagesStore.canViewCurrentChats;
});

const dbInvalidated: ComputedRef<boolean> = computed(() => {
  return connectionStore.dbInvalidated;
});

const isGuestUser: ComputedRef<boolean> = computed(() => {
  return userStore.isGuestUser;
});

const activeEntryId: ComputedRef<string | null> = computed(() => {
  return trackEntriesStore.activeEntryId;
});

const activeAppId: ComputedRef<string | undefined> = computed(() => {
  return tracksStore.activeAppId;
});

const appClass: ComputedRef<string | null> = computed(() => {
  let cls = null;

  if (BrowserDetection.isMobile()) {
    cls = 'mobile';
  }

  if (BrowserDetection.isIosApp()) {
    cls = cls || '';
    cls += ' nativeIos';
    if (BrowserDetection.isIphoneApp()) {
      cls += ' nativeIphone';
    } else if (BrowserDetection.isIpadApp()) {
      cls += ' nativeIpad';
    }
  }

  return cls;
});

const activeTrackId: ComputedRef<string | null> = computed(() => {
  return tracksStore.activeTrackId;
});

const showApp: ComputedRef<boolean> = computed(() => {
  return !!route?.name && !reconnecting.value;
});

const reconnecting: ComputedRef<boolean> = computed(() => {
  return route?.name === RouteNames.RECONNECT_ROUTE_NAME;
});

const showLandscapeBlock: ComputedRef<boolean> = computed(() => {
  // From the OrientationDetection composable
  return inPhoneLandscapeMode.value;
});

const showPortraitBlock: ComputedRef<boolean> = computed(() => {
  // From the OrientationDetection composable
  return inTabletPortraitMode.value;
});

const showOrientationBlock: ComputedRef<boolean> = computed(() => {
  return !uiStateStore.preventOrientationBlock && (showLandscapeBlock.value || showPortraitBlock.value);
});

const guestState: ComputedRef<GuestState> = computed(() => {
  const trackIdForGuestMemberCheck: string | null = isAppGuestRoute.value ? activeTrackId.value : null;
  return {
    guestUser: isGuestUser.value,
    guestMember: trackIdForGuestMemberCheck ? userStore.isGuestMember(trackIdForGuestMemberCheck) : false,
    isAppGuestRoute: isAppGuestRoute.value,
  };
});

const isAppGuestRoute: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.TRACK_APP_GUEST_VIEW_ROUTE_NAME;
});

async function updateGuestOnlineState(): Promise<GuestOnlineStatusDetails | undefined> {
  // Do an extra guest check here; it could be that the user logged in during the guest update interval
  if (isAppGuestRoute.value && userStore.isGuestOfActiveTrack && tracksStore.activeTrackId) {
    return userStore.updateGuestOnlineStatus(tracksStore.activeTrackId);
  }
}

function onVisibilityChange(): void {
  const isActive = document.visibilityState === 'visible';
  uiStateStore.setTabVisible(isActive);
  DataWorker.instance().setUserActive(isActive);
  if (isActive) {
    navigator.serviceWorker.ready.then(serviceworker => {
      try {
        serviceworker.getNotifications().then(allNotifications => {
          allNotifications.forEach(notification => notification.close());
        });
      } catch (ex) {
        // Nothing to do - Some browsers don't implement this and it was causing noise in logs.
      }
    });
  }
}

function isRunningInIFrame(): boolean {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

function overrideVh(): void {
  if (BrowserDetection.isMobile() || BrowserDetection.isIpadApp() || BrowserDetection.isIosApp()) {
    // The vh unit on mobile varies between browsers. Typically it includes the address bar, which isn't
    // constant. Here we create our own unit that means 1% of the visible height.
    nextTick(() => {
      // We need to use body size instead of window innerHeight as when mobile safari uses native zoom it breaks
      // the relationship between body size and the window dimensions.
      const mobileVh = document.body.getBoundingClientRect().height * 0.01;
      document.documentElement.style.setProperty('--vh', `${mobileVh}px`);
      // log.debug(`Overriding vh to ${mobileVh}px`);
    });
  } else {
    // log.debug('No need to override vh');
    document.documentElement.style.setProperty('--vh', '1vh');
  }
}

function localStoreChanged(event: StorageEvent): void {
  if (eventSignalsLogout(event)) {
    wrapupLogout();
  }
}

/**
 * Another tab has triggered a logout. So we need to clean/wrap up local states here.
 * This completes the code run in the main tab via the class ProfileContextMenu.vue:logoutClicked()
 */
async function wrapupLogout(): Promise<void> {
  await userStore.clearUserState();
  window.location.replace(`${Integrations.BE}/logout`);
}

function dragover(event: DragEvent) {
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'none';
  }
}

/**
 * If we are a guest, or a guest member (logged in but not a member of the meeting's track), then we should have a
 * guest token that was requested when navigating to the meeting route.
 *
 * If that's the case, we need to periodically send guest online status updates
 * @param guestState
 * @private
 */
watch(() => guestState.value, async (guestState: GuestState) => {
  // This is called when switching pages... when to actually stop polling?
  if (guestState.guestUser || guestState.guestMember) {
    const params = await updateGuestOnlineState();
    if (params) {
      guestDetails.value = params;
      // This is idempotent as the surrounding watcher is called on app page change
      DataWorker.instance().dispatch('OnlineStatus/startUpdateGuestOnlineStatusInterval',
        params.trackId);
    }
  } else {
    if (guestDetails.value) {
      guestDetails.value = null;
    }
  }
});

watch(() => activeTrackId.value, (newValue: string | null, oldValue?: string | null) => {
  // Don't set the active track ID if we're a guest or a guest member of that track
  if (isGuestUser.value || userStore.isGuestMember(newValue)) {
    // Keep track of guest track ID separately for guest poll/status update
    DataWorker.instance().setActiveGuestTrackId(newValue);
    return;
  }

  DataWorker.instance().setActiveTrackId(newValue, oldValue);
  if (oldValue) {
    chatMessagesStore.handleClosedChatTrackId(oldValue);
  }
  if (newValue) {
    chatMessagesStore.handleNewChatTrackId(newValue);
  }
}, { immediate: true });

watch(() => activeEntryId.value, (newValue: string | null, oldValue?: string | null) => {
  // TODO https://gitlab.com/cafex-communications/fender/fender/-/issues/7285
  // If this is a logged in user, isGuestUser will be false, and we'll set the active entry ID. But, we'll still be a
  // guest for that entry's track, so we probably can't access it and we'll get some horrible errors.
  //
  // This probably isn't the right solution anyway, as we are going to need to set an active entry ID (or at least a
  // 'guest active entry ID' for present mode)
  if (isGuestUser.value) {
    return;
  }

  DataWorker.instance().setActiveEntryId(newValue, oldValue);
}, { immediate: true });

watch(() => activeAppId.value, (newValue: string | undefined, oldValue?: string | undefined) => {
  if (isGuestUser.value) {
    return;
  }

  const newMiniAppId: string | undefined = newValue === DEFAULT_COLLABORATION_APP_ID ? undefined : newValue;
  const previousMiniAppId: string | undefined = oldValue === DEFAULT_COLLABORATION_APP_ID ? undefined : oldValue;

  DataWorker.instance().setActiveMiniAppId(newMiniAppId, previousMiniAppId);
}, { immediate: true });

/*
 * It's possible that chat view permissions may change while a user already has a chat open.
 * Any messages sent while chat is open and permissions are off will need to be caught up when permissions
 * are turned back on.
 */
watch(() => canViewCurrentChats.value, (newValue: ChatViewPermissions, oldValue: ChatViewPermissions) => {
  // If the value changed from null to true, that just means that we have now loaded enough data to do the check
  const activeTrackChatPermissionEnabled = newValue.canViewActiveTrackChat &&
    oldValue.canViewActiveTrackChat === false &&
    newValue.activeTrackId === oldValue.activeTrackId;

  if (activeTrackChatPermissionEnabled && newValue.activeTrackId) {
    chatMessagesStore.handleActiveTrackChatViewPermissionChanged(newValue.activeTrackId);
  }
});

onMounted(() => {
  window.addEventListener('resize', debouncedOverrideVh, true);
  window.addEventListener('storage', localStoreChanged);
  overrideVh();

  if (BrowserDetection.isIosApp()) {
    IosDeviceToken.addWindowFunctions();
  }

  // We want to ignore visibility state for iframe instances of Fender
  const runningInIframe = isRunningInIFrame();
  if (runningInIframe) {
    DataWorker.instance().setUserActive(false);
  } else {
    document.addEventListener('visibilitychange', () => {
      onVisibilityChange();
    });
    // Now the listener has been added set the visibility state for the current endpoint
    onVisibilityChange();
  }

  // This must be done here as it must be done inside the hidden @Component setup lifecycle
  // It cannot be done as a const...
  matomoAnalytics.value = MatomoAnalytics();
});

onBeforeUnmount(() => {
  if (matomoAnalytics.value) {
    matomoAnalytics.value.unwatch();
  }
  window.removeEventListener('storage', localStoreChanged);
  window.removeEventListener('resize', debouncedOverrideVh, true);
});

uiStateStore.loadSettings();
</script>

<style lang="scss">
// Global styles
@import 'styles/_baseFonts.scss';
@import 'styles/_transitions.scss';
@import 'styles/_scrollBarStyle.scss';
@import 'styles/_defaultStyles.scss';

@import 'floating-vue/dist/style.css';

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.25s ease-out;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

// This is used for the chat message auto-complete popup on an at-mention
.tribute-container {
  background-color: $colour-background-light;
  min-width: 20em;
  overflow-y: auto;
  max-height: 12em;
  height: auto;
  border: 1px solid #bec6c8;
  border-radius: $size-border-radius-primary;
  // To make sure popup can appear in dialogs
  z-index: 9999;

  ul {
    list-style: none;
    margin: 0;
    padding-inline-start: 0;
    margin-block-start: 0;
    margin-block-end: 0;
    padding: 0.5rem;
    li {
      display: flex;
      align-items: center;
      padding: 0.5rem;
      white-space: pre;
      &.highlight {
        background-color: $colour-background-light-hover;
      }
      .matchingText {
        background-color: $colour-background-mid;
      }
      .emoji {
        font-size: $size-emoji;
      }
    }
  }
}

// Override styling of the default v-tooltip (floating-vue) "tooltip" theme (when also using default placement)
.v-popper--theme-tooltip[data-popper-placement^='top-start']  {
  // Do not apply the arrow position override to validation error popups
  &:not(.v-popper--theme-error) {
    .v-popper__arrow-container {
      // Ensure arrow not placed beyond bounds of a narrow tooltip
      left: min(30%, 20px) !important;
    }
  }
}

// This is v-tooltip (floating-vue) styling. https://floating-vue.starpad.dev/guide/css#popper-content
.v-popper--theme-info {
  // I didn't want to include the app studio vars at the top level, but wanted this theme available everywhere, so I've
  // hard-coded the variables here.
  .v-popper__inner {
    padding: 1rem;
    box-sizing: border-box;
    background: white;
    border: none;
    border-radius: 4px;
    box-shadow: 0 0 0 1px $colour-border-mid, 0 0 1rem rgba(0, 0, 0, 0.2);
  }

  .v-popper__arrow-container {
    .v-popper__arrow-outer {
      // The anti-aliasing on the angles lines makes #cccccc look far too light. Darken it to compensate
      border-color: #a9a9a9;
    }
    .v-popper__arrow-inner {
      visibility: visible;
      border-color: white;
    }
  }

  p {
    font-size: 14px;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  figure {
    display: grid;
    grid-template-columns: 5rem auto;
    margin-left: 1rem;
    margin-top: 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  code {
    font-weight: bold;
    padding: 0.15rem;
    border-radius: 2px;
    width: fit-content;
  }
}
</style>

<style scoped lang="scss">
  #app {
    // webpack doesn't recognise this property - hence vendor prefix is specifically applied here
    -webkit-print-color-adjust: exact;
    display: flex;
    height: 100%;
    overflow: hidden;
    background-color: $colour-background-light;
  }
  #app.nativeIos {
    background-color: transparent;
  }
  .pageWrapper {
    width: 100%;
    height: 100%;
  }
</style>
